import {
    Component,
    computed,
    DestroyRef,
    EventEmitter,
    inject,
    Input,
    input, OnChanges,
    OnInit,
    Output,
    signal,
    SimpleChanges,
    TemplateRef
} from '@angular/core';
import {PureOriginalsModel} from "../../models/PureOriginals-model";
import {SearchFilterModel} from "../../models/SearchFilter-model";

import {fadeInAnimation} from "../../animations/fadeInAnimation";
import {UpdatePureOriginalModel} from "../../models/UpdatePureOriginal-model";
import {SortDirectionValuesModel} from "../../models/SortDirectionValues-model";
import {AuthService} from "../../service/auth.service";
import {PureOriginalService} from "../../service/pure-original.service";
import {UploadFileDialogService} from "../../service/uploadFileDialog.service";
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-pure-originals-table',
    templateUrl: './pure-originals-table.component.html',
    styleUrls: ['./pure-originals-table.component.css'],
    animations: [fadeInAnimation],
    host: {'[@fadeInAnimation]': ''}
})


export class PureOriginalsTableComponent implements OnInit, OnChanges {

    displayedTableHeaders = input<string[]>([]);
    pureOriginals = input<PureOriginalsModel[]>([])
    displayedOriginalColumns = input<(keyof PureOriginalsModel)[]>([]);
    selectedFilters = input<SearchFilterModel[]>([])
    sortDirectionValues = input<SortDirectionValuesModel>()
    disableSortButton = input<boolean>();
    isLoadingTemplate = input<boolean>(false);
    enableTuningsPreview = input(false);
    readonly expandedPureOriginalId = signal<number | null>(null);
    readonly tableHeaders = computed(() => this.enableTuningsPreview()
        ? ['tuningExpand', ...this.displayedTableHeaders()]
        : this.displayedTableHeaders());


    @Input() actionsTemplate?: TemplateRef<any>;
    @Input() headerActionButton?: boolean;
    @Input() headerButtonResetValue: boolean = false;


    @Output() deletePureOriginalItemOutput = new EventEmitter();
    @Output() openPureOriginalDetailsOutput = new EventEmitter();
    @Output() downloadOriginalFile = new EventEmitter();
    @Output() editPureOriginalDetails = new EventEmitter();
    @Output() changeSortDirection = new EventEmitter();
    @Output() selectedIdsOutput = new EventEmitter<string[]>();
    selectedIds = signal<string[]>([]);
    isModalOpened = signal<boolean>(false);

    authService = inject(AuthService);
    pureOriginalsService = inject(PureOriginalService);
    uploadFileService = inject(UploadFileDialogService);
    private destroyRef = inject(DestroyRef);

    ngOnInit() {
        this.uploadFileService.reset$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.resetPureOriginalsIniFile();
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['pureOriginals'] && !this.pureOriginals().some(row => +row.id === this.expandedPureOriginalId())) {
            this.expandedPureOriginalId.set(null);
        }
        // When the header button reset signal changes to true, clear the selection.
        if (
            changes["headerButtonResetValue"] &&
            changes["headerButtonResetValue"].currentValue === true
        ) {
            this.selectedIds.set([]);
        }
    }

    deletePureOriginalItem(element: PureOriginalsModel, pureOriginalId: number, tableName: string) {
        return this.deletePureOriginalItemOutput.emit({element, pureOriginalId, tableName})
    }

    toggleTunings(pureOriginalId: number, event: Event): void {
        event.stopPropagation();
        if (!this.enableTuningsPreview() || !Number.isSafeInteger(pureOriginalId) || pureOriginalId <= 0) return;
        this.expandedPureOriginalId.update(current => current === pureOriginalId ? null : pureOriginalId);
    }

    openPureOriginalDetails(pureOriginalId: number) {
        this.openPureOriginalDetailsOutput.emit(pureOriginalId);
    }

    editPureOriginalDetailsItem(element: UpdatePureOriginalModel) {
        this.editPureOriginalDetails.emit(element)
    }

    changePureOriginalsTableSortDirection(event: any) {
        this.changeSortDirection.emit(event);
    }

    isSuperAdmin() {
        return this.authService.isSuperAdmin();
    }

    openPureOriginalUploadModal(id: number) {
        this.pureOriginalsService.pureOriginalId.set(id);
        this.isModalOpened.set(true);
    }

    uploadIniFile($event: any) {
        const file = $event.valueOf()["fileToUpload"];
        this.pureOriginalsService.uploadIniFile(file, this.pureOriginalsService.pureOriginalId()).then();
    }

    resetPureOriginalsIniFile() {
        this.pureOriginalsService.refreshIniFile();
    }

    isAllSelected(): boolean {
        return this.pureOriginals().length > 0 && this.selectedIds().length === this.pureOriginals().length;
    }

    toggleSelectAll(event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;

        if (checked) {
            this.pureOriginals().forEach(row => this.selectedIds.update((prevNumber) => [...prevNumber, row.id]))
        } else {
            this.selectedIds.set([]);
        }
        this.emitSelectedIds();
    }

    emitSelectedIds() {
        this.selectedIdsOutput.emit(this.selectedIds());
    }

    fileModalClosed() {
        this.isModalOpened.set(false);
    }

    formatDate(value: any, column: string): string {
        if (value == null || value === '' || value === 'dateUploaded') return value;

        const dateColumns = ['date', 'dateUploaded', 'dateUpdated', 'dateExported', 'dateMatched', 'versionDateUploaded', 'dateuploaded'];
        if (dateColumns.includes(column)) {
            const parts = value.toString().split('-');
            if (parts.length < 6) return value;

            let year, month, day, hours, minutes, seconds;

            if (parseInt(parts[0], 10) > 12) {
                day = parseInt(parts[0], 10);
                month = parseInt(parts[1], 10) - 1;
                year = parseInt(parts[2], 10);
            } else if (parseInt(parts[1], 10) > 12) {
                month = parseInt(parts[0], 10) - 1;
                day = parseInt(parts[1], 10);
                year = parseInt(parts[2], 10);
            } else {
                if (column === 'dateuploaded') {
                    month = parseInt(parts[0], 10) - 1;
                    day = parseInt(parts[1], 10);
                } else {
                    day = parseInt(parts[0], 10);
                    month = parseInt(parts[1], 10) - 1;
                }
                year = parseInt(parts[2], 10);
            }

            hours = parseInt(parts[3], 10);
            minutes = parseInt(parts[4], 10);
            seconds = parseInt(parts[5], 10);

            const utcDate = new Date(Date.UTC(year, month, day, hours, minutes, seconds));

            if (isNaN(utcDate.getTime())) return value;

            const localDay = utcDate.getDate().toString().padStart(2, '0');
            const localMonth = (utcDate.getMonth() + 1).toString().padStart(2, '0');
            const localYear = utcDate.getFullYear();
            const localHours = utcDate.getHours().toString().padStart(2, '0');
            const localMinutes = utcDate.getMinutes().toString().padStart(2, '0');
            const localSeconds = utcDate.getSeconds().toString().padStart(2, '0');

            return `${localDay}-${localMonth}-${localYear} ${localHours}:${localMinutes}:${localSeconds}`;
        }

        return value;
    }
}
